import React, { ReactNode } from 'react';
import { Translate } from 'react-redux-i18n';
import Cropper from 'react-easy-crop';
import { connect } from 'react-redux';
import { Area, Point } from 'react-easy-crop/types';
import { createCroppedImage, getOrientation } from '../../services/cropService';
import Modal from '../common/modals/Modal';
import camera from '../../img/icones/camera2.svg';
import H4Title from '../common/H4Title';
import Button from '../common/Button';
import { MonIdentite } from '../../network/api/types/compte/MonIdentite';
import compteApi from '../../network/api/compteApi';
import { updateMyIndentity } from '../../services/user/userService';
import { CompteMonidentiteModifyDTO } from '../../network/api/types/compte/CompteMonidentiteModifyDTO';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  pictureUrl?: string;
  image: string,
  myIdentity?: MonIdentite,
  imageChanged?: (image: string) => void,
  updateMyIndentity?: (infos: CompteMonidentiteModifyDTO) => void,
};

type State = {
  picture?: string;
  crop: Point;
  zoom: number;
  croppedAreaPixels?: Area;
  orientation?: number;
  error: boolean;
};

const acceptedTypes = ['image/png', 'image/jpeg'];

const maximumSize = 2 ** 20; // 1Mo

const initialState: State = {
  crop: { x: 0, y: 0 },
  zoom: 1,
  picture: undefined,
  croppedAreaPixels: undefined,
  orientation: undefined,
  error: false,
};

const maxZoomSize = 50;
const minZoomSize = 0.5;
const zoomButtonRatio = 1.5;

class PicturePopin extends React.Component<Props, State> {
  fileInput;

  state = initialState;

  triggerInputFile = (): void => {
    this.fileInput.click();
  };

  handleFileLoad = (e: React.ChangeEvent<HTMLInputElement>): void => this.onFileLoad(
    e.target.files[0],
  );

  onFileLoad = (file?: Blob): void => {
    if (file) {
      if (file.size > maximumSize) {
        this.setState({ error: true });
      } else if (acceptedTypes.includes(file.type)) {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.setState({ ...initialState, picture: reader.result.toString() });
        };
        reader.readAsDataURL(file);

        const arrayBufferReader = new FileReader();
        arrayBufferReader.onloadend = (e: ProgressEvent<FileReader>) => {
          getOrientation(e.target.result as ArrayBuffer, (orientation: number) => {
            this.setState({
              orientation,
              error: false,
            });
          });
        };
        arrayBufferReader.readAsArrayBuffer(file);
      }
    }
  };

  savePicture = (): void => {
    this.cropImage()
      .then((croppedPicture: string) => {
        if (this.props.myIdentity) {
          return this.props.updateMyIndentity({ ...this.props.myIdentity, photo: croppedPicture });
        }
        this.props.imageChanged(croppedPicture);
        return null;
      })
      // TODO reload myidentity everywhere
      .finally(() => {
        this.closePopin();
      });
  };

  deletePicture = (): void => {
    if (this.props.myIdentity) {
      this.props.updateMyIndentity({ ...this.props.myIdentity, photo: '' });
      this.closePopin();
    } else {
      this.props.imageChanged(null);
      this.closePopin();
    }
  };

  closePopin = (): void => {
    const { onClose } = this.props;
    if (this.fileInput) {
      this.fileInput.value = '';
    }
    this.setState(initialState);
    onClose();
  };

  onDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    this.onFileLoad(e.dataTransfer.files[0]);
  };

  onCropChange = (crop: Point): void => {
    this.setState({ crop });
  };

  onZoomChange = (zoom: number): void => {
    this.setState({ zoom });
  };

  onCropComplete = (croppedArea: Area, croppedAreaPixels: Area): void => {
    this.setState({ croppedAreaPixels });
  };

  cropImage = (): Promise<string> => {
    const { picture, croppedAreaPixels, orientation } = this.state;
    return createCroppedImage(picture, croppedAreaPixels, orientation);
  };

  render(): ReactNode {
    const {
      picture, crop, zoom, error,
    } = this.state;
    const { pictureUrl } = this.props;
    return (
      <Modal
        isOpen={this.props.isOpen}
        onClose={this.closePopin}
      >
        <div className="input-file-container">
          <H4Title className="rattachement-title">
            <Translate value="input.picture" />
          </H4Title>
          <h5 className="rattachement-title">
            <Translate value="input.subtitle" />
          </h5>
          <input
            className="input-file"
            ref={(fileInput) => {
              this.fileInput = fileInput;
            }}
            type="file"
            accept="image/jpeg,image/png"
            onChange={this.handleFileLoad}
          />
          <div
            className="dropper"
            onDrop={this.onDrop}
            onDragOver={(e: React.DragEvent<HTMLDivElement>) => e.preventDefault()}
          >
            <div className="cropper-container">
              {
                picture
                  ? (
                    <>
                      <Cropper
                        image={picture}
                        crop={crop}
                        zoom={zoom}
                        aspect={4 / 3}
                        classes={{
                          containerClassName: 'cropper',
                        }}
                        cropSize={{ width: 160, height: 160 }}
                        restrictPosition={false}
                        showGrid={false}
                        zoomSpeed={0.1}
                        minZoom={minZoomSize}
                        maxZoom={maxZoomSize}
                        onCropChange={this.onCropChange}
                        onZoomChange={this.onZoomChange}
                        onCropComplete={this.onCropComplete}
                      />
                      <button
                        className="picture-edit"
                        type="button"
                        onClick={this.triggerInputFile}
                      >
                        <img
                          className="add-foto"
                          src={camera}
                          alt="add-foto"
                        />
                      </button>
                      <div className="zoom-button-container">
                        <button
                          type="button"
                          onClick={
                            () => this.setState((prevState) =>
                              ({
                                zoom: prevState.zoom < (maxZoomSize / zoomButtonRatio)
                                  ? prevState.zoom * zoomButtonRatio
                                  : maxZoomSize,
                              }))
                          }
                        >
                          +
                        </button>
                        <button
                          type="button"
                          onClick={() => this.setState((prevState) => ({
                            zoom: prevState.zoom > (minZoomSize / zoomButtonRatio)
                              ? prevState.zoom / zoomButtonRatio
                              : minZoomSize,
                          }))}
                        >
                          -
                        </button>
                      </div>
                    </>
                  )
                  : (
                    <button type="button" onClick={this.triggerInputFile} className="picture">
                      <img
                        src={this.props.image}
                        alt="ppicture"
                      />
                    </button>
                  )
              }
            </div>
          </div>
        </div>
        <Translate className={error ? 'picture-popin-error-message' : ''} value="wsError.ENTI_05_ERR_13" />
        <div className="button-container input-file">
          <Button
            type="third"
            onClick={this.props.onClose}
            i18nKey="actions.CANCEL"
          />
          <Button
            type="third"
            onClick={this.deletePicture}
            i18nKey="actions.DELETE"
          />
          <Button
            type="primary"
            onClick={this.savePicture}
            i18nKey="actions.SAVE"
          />
        </div>
      </Modal>
    );
  }
}

export default connect((state) => ({}), { updateMyIndentity })(PicturePopin);
